import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ShareButtons from "@/components/base/ShareButtons";
import ArticleBody from "@/components/feature/ArticleBody";
import { newsList } from "@/mocks/news";
import usePageTitle from "@/hooks/usePageTitle";

export default function NewsDetail() {
  const { id } = useParams();
  const article = newsList.find((item) => item.id === id);
  usePageTitle(article ? `新闻详情 - ${article.title}` : "相关资讯");

  if (!article) {
    return (
      <div className="px-4 md:px-8 py-16">
        <div className="mx-auto max-w-3xl">
          <EmptyState
            title="未找到该资讯"
            description="这条资讯可能已下线或链接有误，返回资讯列表查看更多最新内容。"
            actionLabel="返回相关资讯"
            actionTo="/news"
          />
        </div>
      </div>
    );
  }

  const related = newsList.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <article className="w-full">
      <section className="border-b border-background-200 bg-background-100 px-4 md:px-8 py-8 md:py-10">
        <div className="mx-auto max-w-4xl">
          <Breadcrumb items={[{ label: "相关资讯", to: "/news" }, { label: article.title }]} />
        </div>
      </section>

      <div className="px-4 md:px-8 py-10 md:py-14">
        <div className="mx-auto max-w-4xl flex flex-col gap-8">
          <header className="flex flex-col gap-5">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
              {article.type}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold leading-snug text-foreground-950">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 border-b border-background-200 pb-6 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-newspaper-line" />
                来源：{article.source}
              </span>
            </div>
          </header>

          <div className="h-64 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="flex flex-col gap-6">
            <p className="text-sm md:text-base leading-relaxed text-foreground-700">
              {article.summary}
            </p>
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                要点速览
              </span>
              <ul className="mt-3 flex flex-col gap-2">
                {article.points.map((point) => (
                  <li key={point} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <ArticleBody sections={article.content} />

          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <ShareButtons title={article.title} />
          </div>

          <section className="flex flex-col gap-5">
            <h2 className="font-heading text-xl font-semibold text-foreground-950">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <div className="h-28 w-full overflow-hidden rounded-md">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <span className="text-xs font-medium text-accent-600">{item.type}</span>
                  <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-600">
                    {item.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>

          <Link
            to="/news"
            className="inline-flex w-fit items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回相关资讯
          </Link>
        </div>
      </div>
    </article>
  );
}